import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Check, ChevronDown, ChevronRight, Minus, Target, X } from 'lucide-react';
import type { LucideIcon } from 'lucide-react';

import { useApiErrorMessage } from '@/shared/i18n/useApiErrorMessage';
import { Button, Skeleton, useToastStore } from '@/shared/ui';

import { commitmentBucket, sortForPanel, waitingAge } from '../model/ordering';
import type { CommitmentBucket } from '../model/ordering';
import type { CommitmentSummary, ReopenOutcome } from '../model/types';
import { useElementCommitments, useReopenCommitment } from '../queries/useCommitments';
import { CloseButtons } from './CloseButtons';
import styles from './ElementCommitments.module.css';
import { TILE_ICONS } from './tileIcons';
import { StartIterationControl } from './StartIterationControl';

/**
 * Zobowiązania elementu — **sekcja** panelu-inspektora (decyzja 159, 2026-08-26).
 *
 * Do 2026-08-26 był to cały panel **E1** (`CommitmentsPanel`, decyzja 60: przypięty po prawej
 * stronie drzewka). Werdykt usera do makiety B0 rozszerzył ten panel o szczegóły elementu — ramę
 * (nagłówek z tytułem, „Wejdź", zamknięcie, stopka) trzyma od tej pory `elements/ElementInspector`,
 * a tu zostaje wyłącznie to, co o zobowiązaniach. Akcje zostają te same: jeśli zniknęłyby razem
 * z ramą, panel przestałby być miejscem, w którym domyka się robotę.
 *
 * ⭐ Rola sekcji wynika z decyzji 63: *„na drzewku i w szczegółach elementu widać zobowiązania
 * WSZYSTKICH"*. Jest więc **informacyjna dla cudzych, wykonawcza dla swoich** — przy większości
 * wierszy nie będzie przycisku akcji i tak ma być. Przełącznik „moje / wszystkie" świadomie nie
 * powstaje; „co jest na mnie" mieszka na pulpicie i listach z menu.
 *
 * Kolejność wierszy rozstrzyga decyzja 66 (stan, potem „wisi od") i liczy ją `model/ordering`,
 * poza Reactem — tu zostaje samo pokazywanie.
 */

/**
 * Znak kubełka — kształt niesie stan, kolor go wzmacnia (decyzja 58).
 *
 * ⭐ Ikona przychodzi z `TILE_ICONS`, czyli **stamtąd, co kafel pulpitu i pozycja menu**:
 * wiersz panelu i lista z menu opisują ten sam stan, więc nie mogą wyglądać na dwie różne
 * rzeczy. To ta sama nauka, którą zapisał komentarz przy `TILE_ICONS` (2026-08-06).
 *
 * ⚠ Do 2026-08-19 były tu ZNAKI CZCIONKI (`⚠ ⚡ ✓ 💤`) — mimo spec 12B.7 („zero emoji i zero
 * glifów czcionki"). Kosztowały dokładnie to, co zapowiadał zakaz: `⚠` renderuje się na
 * Windows jako **kolorowe emoji**, które ignoruje `color`, więc `markDraugr` nie miało jak
 * pomalować znaku na alarmowo — kolor jako wzmocnienie stanu po prostu nie działał.
 *
 * ⚠ `closed` nie pojawia się w liście otwartych (historia ma własny wiersz i własne dwa
 * znaki: zrobione kontra nieaktualne) — stoi tu, bo mapa musi pokryć cały typ kubełka.
 */
const BUCKET_ICONS: Record<CommitmentBucket, LucideIcon> = {
  draugr: TILE_ICONS.draugr,
  fired: TILE_ICONS.fired,
  todo: TILE_ICONS.hanging,
  sleeping: TILE_ICONS.dormant,
  closed: Minus,
};

/** Liczniki w nagłówku biorą znak stamtąd, co wiersze — nazwa z wielkiej, bo to komponent. */
const SleepingIcon = BUCKET_ICONS.sleeping;
const DraugrIcon = BUCKET_ICONS.draugr;

/** Od ilu dni „wisi od" przestaje być informacją, a zaczyna być ostrzeżeniem. */
const STALE_AFTER_DAYS = 30;

interface ElementCommitmentsProps {
  elementId: number;
  /**
   * Identyfikator nagłówka sekcji. Panel-inspektor przewija do niego po kliknięciu w liczby pracy
   * przy wierszu drzewka (decyzja 159: liczby są skrótem do roboty, tylko nie tracą kontekstu elementu).
   */
  headingId?: string;
}

export function ElementCommitments({
  elementId,
  headingId = 'element-commitments',
}: ElementCommitmentsProps) {
  const { t } = useTranslation();
  const describeError = useApiErrorMessage();

  const open = useElementCommitments(elementId, 'open');
  const [historyShown, setHistoryShown] = useState(false);
  // Historia dociąga się dopiero po rozwinięciu: zamkniętych bywa wielokrotnie
  // więcej niż otwartych, a domyślnie są schowane (decyzja 66).
  const closed = useElementCommitments(elementId, 'closed', historyShown);

  const rows = sortForPanel(open.data ?? []);

  const counts = {
    active: rows.filter((row) => {
      const bucket = commitmentBucket(row);
      return bucket === 'todo' || bucket === 'fired';
    }).length,
    sleeping: rows.filter((row) => commitmentBucket(row) === 'sleeping').length,
    draugr: rows.filter((row) => commitmentBucket(row) === 'draugr').length,
  };

  return (
    <section className={styles['section']} aria-labelledby={headingId}>
      <header className={styles['head']}>
        <div className={styles['headline']}>
          <h3 id={headingId} className={styles['heading']}>
            {t('commitments.title')}
          </h3>

          <span className={styles['count']}>{t('commitments.summary', { count: rows.length })}</span>
        </div>

        <div className={styles['counters']}>
          <span className={styles['counter']}>
            {t('commitments.bucket.todo')} <b>{counts.active}</b>
          </span>
          {/* ⚠ Ikona jest `aria-hidden`, więc goła liczba nie powiedziałaby czytnikowi ekranu
              NICZEGO — nazwa kubełka wchodzi tekstem tylko dla niego. Wcześniej niosło ją
              emoji, czyli czytnik mówił „symbol śpi", a nie „uśpione". */}
          <span className={styles['counter']}>
            <SleepingIcon size={13} aria-hidden="true" />
            <span className="visually-hidden">{t('commitments.bucket.sleeping')}</span>
            <b>{counts.sleeping}</b>
          </span>
          <span className={`${styles['counter']} ${styles['counterDraugr']}`}>
            <DraugrIcon size={13} aria-hidden="true" />
            <span className="visually-hidden">{t('commitments.bucket.draugr')}</span>
            <b>{counts.draugr}</b>
          </span>
        </div>

        {/*
          „Zacznij iterację" (dec. 93): pokazywane, gdy JEST co odciąć i człowiek MOŻE — `canClose`
          liczy serwer z własności elementu (ta sama reguła co odcinanie, spec 9.2), więc dowolny
          wiersz z `canClose` znaczy „właściciel albo admin". Bez iteracji przycisk nie kusi pustą
          odmową (`iteration.empty` i tak by ją odbiło).
        */}
        {rows.some((row) => row.canClose) &&
        rows.some((row) => row.iterationNumber !== null && row.firedAtUtc === null) ? (
          <StartIterationControl elementId={elementId} />
        ) : null}
      </header>

      <div className={styles['body']}>
        {open.isPending ? (
          <div className={styles['loading']} aria-busy="true">
            <Skeleton height="2.5rem" announce />
            <Skeleton height="2.5rem" />
            <Skeleton height="2.5rem" />
          </div>
        ) : open.isError ? (
          <div className={styles['failure']} role="alert">
            <p>{describeError(open.error)}</p>
            <Button onClick={() => void open.refetch()}>{t('common.action.retry')}</Button>
          </div>
        ) : rows.length === 0 ? (
          <p className={styles['empty']}>{t('commitments.empty')}</p>
        ) : (
          <ul className={styles['list']}>
            {rows.map((commitment) => (
              <Row key={commitment.id} commitment={commitment} />
            ))}
          </ul>
        )}
      </div>

      <div className={styles['history']}>
        <button
          type="button"
          className={styles['historyToggle']}
          aria-expanded={historyShown}
          onClick={() => setHistoryShown((shown) => !shown)}
        >
          {historyShown ? (
            <ChevronDown size={14} aria-hidden="true" />
          ) : (
            <ChevronRight size={14} aria-hidden="true" />
          )}
          {t('commitments.history')}
          {closed.data ? (
            <span className={styles['historyCount']}>
              {t('commitments.historyCount', { count: closed.data.length })}
            </span>
          ) : null}
        </button>

        {historyShown ? <History rows={closed.data} isPending={closed.isPending} /> : null}
      </div>
    </section>
  );
}

/**
 * Jeden wiersz listy otwartych.
 *
 * Wiersz odpowiada na trzy pytania naraz: **co** jest do zrobienia, **dlaczego** to tu stoi
 * (obudzone / czeka na coś / bez warunku) i **jak długo**. Środkowe jest tym, czego nie ma
 * żaden zwykły menedżer zadań — bez niego draugr wygląda jak zwykłe zaległe zadanie.
 */
function Row({ commitment }: { commitment: CommitmentSummary }) {
  const { t } = useTranslation();

  const bucket = commitmentBucket(commitment);
  const BucketIcon = BUCKET_ICONS[bucket];
  const age = waitingAge(commitment, new Date());
  const stale = age.unit === 'days' && age.value >= STALE_AFTER_DAYS;

  return (
    <li className={`${styles['row']} ${bucket === 'draugr' ? styles['rowDraugr'] : ''}`}>
      <span className={`${styles['mark']} ${markClass(bucket)}`} aria-hidden="true">
        <BucketIcon size={14} />
      </span>

      <span className={styles['title']} title={commitment.title}>
        {commitment.title}
        {/* Stan niesie na ekranie znak i kolor — czytnik ekranu dostaje go słowem. */}
        <span className="visually-hidden"> · {t(`commitments.bucket.${bucket}`)}</span>
      </span>

      <span className={`${styles['age']} ${stale ? styles['ageOld'] : ''}`}>
        {t(`commitments.age.${age.unit}`, { count: age.value })}
      </span>

      <span className={styles['why']}>{describeWhy(commitment, bucket, t)}</span>

      {/*
        ⭐ Pochodzenie zadania — jedyna rzecz, którą zwykły użytkownik widzi z kampanii
        (decyzja 109). ⛔ Świadomie BEZ odsyłacza do ekranu kampanii: dla nie-architekta
        prowadziłby w 403, a drzwi, które i tak są zamknięte, się nie rysuje.

        ⚠ WŁASNY WIERSZ, nie dopisek do „dlaczego" — tamten wiersz ma `text-overflow: ellipsis`
        i w wąskim panelu **ucinał pochodzenie w połowie**. Jedyna rzecz, którą zwykły user ma
        stąd wynieść, nie może zależeć od szerokości panelu. (Złapane na żywym ekranie.)
      */}
      {commitment.origin ? (
        <span className={styles['origin']}>
          <Target size={11} aria-hidden="true" className={styles['originIcon']} />
          <span className={styles['originText']}>
            {t('commitments.fromCampaign', { title: commitment.origin.campaignTitle })}
          </span>
        </span>
      ) : null}

      {/*
        Przyciski wspólne z listami z menu (`CloseButtons`) — nie dla oszczędności, tylko
        dlatego, że powód zamknięcia jest **tą samą regułą** w obu miejscach. Nic nie
        renderują przy cudzym zobowiązaniu i tak ma być (decyzja 63).
      */}
      <span className={styles['actions']}>
        <CloseButtons commitment={commitment} />
      </span>
    </li>
  );
}

function History({
  rows,
  isPending,
}: {
  rows: readonly CommitmentSummary[] | undefined;
  isPending: boolean;
}) {
  const { t } = useTranslation();

  if (isPending) {
    return (
      <div className={styles['loading']} aria-busy="true">
        <Skeleton height="1.5rem" announce />
      </div>
    );
  }

  if (!rows || rows.length === 0) {
    return <p className={styles['empty']}>{t('commitments.emptyHistory')}</p>;
  }

  return (
    <ul className={styles['historyList']}>
      {rows.map((commitment) => (
        <HistoryRow key={commitment.id} commitment={commitment} />
      ))}
    </ul>
  );
}

function HistoryRow({ commitment }: { commitment: CommitmentSummary }) {
  const { t } = useTranslation();
  const describeError = useApiErrorMessage();
  const show = useToastStore((state) => state.show);
  const reopen = useReopenCommitment();

  const age = waitingAge(commitment, new Date());

  return (
    <li className={styles['historyRow']}>
      {/* Dwie osie zamknięcia (spec sek. 3): zrobione kontra nieaktualne. ⚠ Znak jest
          jedyną różnicą w wierszu, więc słowo obok niesie ją dla czytnika ekranu. */}
      <span className={styles['historyMark']} aria-hidden="true">
        {commitment.closeReason === 'done' ? <Check size={13} /> : <X size={13} />}
      </span>

      <span className={styles['historyTitle']} title={commitment.title}>
        {commitment.title}
      </span>

      <span className={styles['historyReason']}>
        {t(`commitments.closedAs.${commitment.closeReason ?? 'done'}`)}
      </span>

      {commitment.canClose ? (
        <Button
          variant="ghost"
          disabled={reopen.isPending}
          onClick={() =>
            reopen.mutate(
              { id: commitment.id, hostElementId: commitment.hostElementId },
              {
                onSuccess: (outcome) =>
                  show({ message: describeReopen(outcome, t), tone: 'success' }),
                // ⛔ Odmowa jest tu NORMALNYM wynikiem, nie awarią: jeśli obudzone
                // zobowiązanie zdążono zrobić, kontrakt Surta jest wykonany i cofnąć
                // się nie da (decyzja 22). Komunikat przychodzi kodem błędu z serwera.
                onError: (error) => show({ message: describeError(error), tone: 'danger' }),
              },
            )
          }
        >
          {t('commitments.action.reopen')}
        </Button>
      ) : null}

      <span className={styles['historyAge']}>
        {t(`commitments.age.${age.unit}`, { count: age.value })}
      </span>
    </li>
  );
}

function markClass(bucket: CommitmentBucket): string {
  switch (bucket) {
    case 'draugr':
      return styles['markDraugr'] ?? '';
    case 'fired':
      return styles['markFired'] ?? '';
    case 'sleeping':
      return styles['markSleeping'] ?? '';
    default:
      return '';
  }
}

/**
 * ⚠ Opcje są **wymagane**, mimo że i18next ich nie wymaga. Przy
 * `exactOptionalPropertyTypes` sygnatura z opcjonalnym parametrem nie przyjmuje `t`
 * z `useTranslation` — wołający podaje więc `{}` tam, gdzie nie ma czego wstawić.
 */
type Translate = (key: string, options: Record<string, unknown>) => string;

/** „Dlaczego ten wiersz tu stoi" — zdanie pod tytułem. */
function describeWhy(
  commitment: CommitmentSummary,
  bucket: CommitmentBucket,
  t: Translate,
): string {
  if (bucket === 'draugr') {
    return commitment.surtSourceTitle
      ? t('commitments.why.orphanedBy', { source: commitment.surtSourceTitle })
      : t('commitments.why.orphaned', {});
  }

  if (bucket === 'fired' && commitment.surtSourceTitle) {
    return t('commitments.why.firedBy', { source: commitment.surtSourceTitle });
  }

  if (bucket === 'sleeping' && commitment.surtSourceTitle) {
    return t('commitments.why.waitingFor', { source: commitment.surtSourceTitle });
  }

  if (commitment.iterationNumber !== null) {
    return t('commitments.why.iteration', { number: commitment.iterationNumber });
  }

  return t('commitments.why.unconditional', {});
}

/**
 * Skutki uboczne ponownego otwarcia. Odpowiednik `describeOutcome` z `CloseButtons`,
 * ale zostaje tutaj: ponowne otwarcie da się zrobić **wyłącznie z historii**,
 * bo listy z menu pokazują pozycje otwarte.
 */
function describeReopen(outcome: ReopenOutcome, t: Translate): string {
  const parts = [t('commitments.result.reopened', { title: outcome.commitment.title })];

  if (outcome.sentBackToSleep.length > 0) {
    parts.push(
      t('commitments.result.backToSleep', { count: outcome.sentBackToSleep.length }),
    );
  }

  if (outcome.orphansCleared.length > 0) {
    parts.push(
      t('commitments.result.orphansCleared', { count: outcome.orphansCleared.length }),
    );
  }

  return parts.join(' · ');
}
